<template>
  <div>
    <form-section :title="$t('Imports_Title_Description')">
      <rest-form-item
        :label="$t('Imports_Field_Name')"
        :hint="$t('Imports_Field_Name_Description')"
        prop="name"
      >
        <el-input
          v-model="model.name"
          :placeholder="$t('Imports_Field_Name')"
        />
      </rest-form-item>
    </form-section>

    <form-section
      :title="$t('Imports_Title_DataType')"
      :description="$t('Imports_Title_DataType_Description')"
    >
      <el-row>
        <el-col :span="24">
          <rest-form-item prop="type" :label="$t('Automations_Field_Identity')">
            <el-row :gutter="10">
              <el-col :span="6">
                <radio-group v-model="model.type">
                  <radio
                    label="profiles"
                    :title="$t('Audiences_Type_Users')"
                    :border="true"
                  ></radio>
                </radio-group>
              </el-col>
              <el-col :span="6">
                <radio-group v-model="model.type">
                  <radio
                    label="profile_attributes"
                    :title="$t('Imports_Type_UserAttributes')"
                    :border="true"
                  ></radio>
                </radio-group>
              </el-col>
              <el-col :span="12">
                <radio-group v-model="model.type">
                  <radio-custom :border="true" label="events">
                    <div class="radio-input">
                      <el-row>
                        <el-col :span="8">
                          <div class="custom-event">
                            {{ $t('Imports_Field_Radio_Events') }}
                          </div>
                        </el-col>
                        <el-col :span="16">
                          <el-select
                            :disabled="model.type !== 'events'"
                            v-model="model.config.event"
                            :placeholder="
                              $t('Campaign_EventSetup_SelectEvent_Placeholder')
                            "
                            :remote-method="fetchEventsOnSearch"
                            clearable
                            filterable
                            remote
                          >
                            <el-option
                              v-for="(item, index) in events"
                              :key="index"
                              :label="item.name"
                              :value="item.name"
                            >
                              <el-tooltip
                                :content="item.name"
                                :open-delay="1000"
                              >
                                <span class="label">{{ item.name }}</span>
                              </el-tooltip>
                            </el-option>
                          </el-select>
                        </el-col>
                      </el-row>
                    </div>
                  </radio-custom>
                </radio-group>
                <rest-form-item prop="config.event" />
              </el-col>
            </el-row>
          </rest-form-item>
        </el-col>
      </el-row>
    </form-section>

    <form-section
      :title="$t('Imports_Title_DataSource')"
      :description="$t('Imports_Title_DataSource_Description')"
      v-loading="loading"
    >
      <template
        slot="controls"
        v-if="model.source.type === 'fs' || model.source.type === 'db'"
      >
        <rest-form-item
          v-show="model.config.columns < 1"
          prop="config.columns"
        />
        <el-button
          type="primary"
          plain
          @click="getSchema"
          :disabled="disableButton"
          icon="el-icon-refresh-right"
        >
          {{ $t('Imports_Button_RefreshSchema') }}
        </el-button>
      </template>

      <el-row>
        <el-col :span="6">
          <rest-form-item :label="$t('Imports_Field_Type')" prop="source.type">
            <enum-select v-model="model.source.type" :options="sourceEnum" />
          </rest-form-item>
        </el-col>

        <el-col :span="6" v-if="model.source.type === 'upload'">
          <rest-form-item
            :label="$t('Imports_Field_Type_Upload')"
            prop="config.columns"
          >
            <csv-upload
              :loading.sync="loading"
              :columns.sync="model.config.columns"
              @init="init"
              :file-id.sync="model.source.file_id"
              :import-type="model.type"
              :event-name="model.config.event"
            />
          </rest-form-item>
        </el-col>

        <el-col :span="6" v-else-if="model.source.type === 'fs'">
          <rest-form-item
            :label="$t('Imports_Field_FileStorage')"
            prop="source.integration_id"
          >
            <el-select v-model="model.source.integration_id" value-key="name">
              <el-option
                v-for="item in steps[currentStep.index].props.integrations"
                :key="item.id"
                :label="item.name"
                :value="item.id"
              />
            </el-select>
          </rest-form-item>
        </el-col>

        <el-col :span="6" v-else-if="model.source.type === 'db'">
          <rest-form-item
            :label="$t('Imports_Field_DbIntegration')"
            prop="source.integration_id"
          >
            <el-select v-model="model.source.integration_id" value-key="name">
              <el-option
                v-for="item in steps[currentStep.index].props.dbIntegrations"
                :key="item.id"
                :label="item.name"
                :value="item.id"
              />
            </el-select>
          </rest-form-item>
        </el-col>
      </el-row>

      <el-row v-if="model.source.type === 'fs'">
        <el-col :span="6">
          <rest-form-item
            :label="$t('Imports_Field_FileDirectory')"
            :hint="$t('Imports_Field_FileDirectory_Hint')"
            prop="source.file_directory"
          >
            <el-input
              v-model="model.source.file_directory"
              :placeholder="$t('Imports_Field_FileDirectory_Placeholder')"
            />
          </rest-form-item>
        </el-col>

        <el-col :span="6">
          <rest-form-item
            :label="$t('Imports_Field_FileName')"
            :hint="$t('Imports_Field_FileName_Hint')"
            prop="source.file_name"
          >
            <el-input v-model="model.source.file_name" />
          </rest-form-item>
        </el-col>
      </el-row>

      <el-row v-if="model.source.type === 'db'">
        <el-col :span="12">
          <rest-form-item
            :label="$t('Imports_Field_DbQuery')"
            :hint="$t('Imports_Field_DbQuery_Hint')"
            prop="source.query"
          >
            <el-input
              v-model="model.source.query"
              type="textarea"
              :rows="6"
              :placeholder="$t('Imports_Field_DbQuery_Placeholder')"
            />
          </rest-form-item>
        </el-col>
      </el-row>

      <el-row v-if="model.source.type === 'db' && !sourceEnum.upload">
        <el-col :span="6">
          <rest-form-item
            :label="$t('Imports_Field_SyncMode')"
            :hint="$t('Imports_Field_SyncMode_Hint')"
            prop="source.sync_mode"
          >
            <el-select v-model="model.source.sync_mode">
              <el-option
                :label="$t('Imports_Field_SyncMode_Full')"
                value="full"
              />
              <el-option
                :label="$t('Imports_Field_SyncMode_Delta')"
                value="delta"
              />
            </el-select>
          </rest-form-item>
        </el-col>

        <el-col :span="6" v-if="model.source.sync_mode === 'delta'">
          <rest-form-item
            :label="$t('Imports_Field_DeltaColumn')"
            :hint="$t('Imports_Field_DeltaColumn_Hint')"
            prop="source.delta_column"
          >
            <el-input v-model="model.source.delta_column" />
          </rest-form-item>
        </el-col>
      </el-row>

      <el-row v-if="model.source.type === 'db'">
        <el-col :span="6">
          <rest-form-item
            :label="$t('Imports_Field_RowLimit')"
            :hint="$t('Imports_Field_RowLimit_Hint')"
            prop="source.row_limit"
          >
            <el-input-number
              v-model="model.source.row_limit"
              :min="100"
              :max="10000000"
              :step="1000000"
              style="width: 100%"
            />
          </rest-form-item>
        </el-col>
      </el-row>
    </form-section>
  </div>
</template>

<script>
import FormSection from '@/common/components/form/FormSection'
import RestFormItem from '@/common/components/rest/RestFormItem'
import CsvUpload from '@/modules/imports/components/CsvUpload'
import EnumSelect from '@/common/components/form/EnumSelect'
import {
  sourceTypesLabels,
  selectionRulesLabels
} from '@/modules/imports/enum/ImportsEnum'
import RestApi from '@/core/services/RestApi'
import { projectEndpoint } from '@/core/helpers'
import RadioGroup from '@/common/components/form/RadioGroup'
import Radio from '@/common/components/form/Radio'
import RadioCustom from '@/modules/campaign-manage/components/locations/RadioCustom'

export default {
  name: 'StepImportDataSource',

  components: {
    RadioCustom,
    Radio,
    RadioGroup,
    FormSection,
    RestFormItem,
    CsvUpload,
    EnumSelect
  },

  props: {
    model: {
      type: Object,
      required: true
    },

    steps: {
      type: Array,
      required: true
    },

    currentStep: {
      type: Object,
      required: true
    }
  },

  mounted() {
    this.$store.dispatch('reloadProjectConfig')
    this.fetchEvents({ sort: 'title' })
  },

  data() {
    return {
      sourceTypesLabels,
      selectionRulesLabels,
      loading: false,
      events: [],
      search: ''
    }
  },

  watch: {
    type() {
      this.init()
    },
    'model.type': {
      handler(identity) {
        if (identity !== 'event') {
          this.model.config.event = ''
        }
      }
    },

    'model.config.columns': {
      deep: true,
      handler() {
        this.updateCols()
      }
    }
  },

  computed: {
    schemaEndpoint() {
      return projectEndpoint({
        method: 'post',
        url: `imports/preview`
      })
    },

    disableButton() {
      if (this.model.source.type === 'db') {
        return !this.model.source.integration_id || !this.model.source.query
      }
      return !this.model.source.integration_id || !this.model.source.file_name
    },

    type() {
      return this.model.source.type
    },

    sourceEnum() {
      if (
        this.steps[this.currentStep.index].props.hasOwnProperty('sources') &&
        Object.keys(this.steps[this.currentStep.index].props.sources).length > 0
      ) {
        return this.steps[this.currentStep.index].props.sources
      } else {
        return this.sourceTypesLabels
      }
    },

    eventsEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: 'events'
      })
    }
  },

  methods: {
    fetchEvents(params) {
      RestApi({
        ...this.eventsEndpoint,
        params: params
      })
        .then(({ data }) => {
          this.events = data
        })
        .catch(error => {
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
    },

    fetchEventsOnSearch(query) {
      this.search = query
      this.fetchEvents({ title: query })
    },

    init() {
      this.model.config.columns = []
    },

    getSchemaData() {
      if (this.model.source.type === 'db') {
        return {
          type: this.model.source.type,
          integration_id: this.model.source.integration_id,
          query: this.model.source.query
        }
      }
      return {
        type: this.model.source.type,
        integration_id: this.model.source.integration_id,
        file_directory: this.model.source.file_directory,
        file_name: this.model.source.file_name,
        import_type: this.model.type,
        event: this.model.config.event
      }
    },

    getSchema() {
      this.init()
      this.loading = true
      RestApi({
        ...this.schemaEndpoint,
        ...{
          data: this.getSchemaData()
        }
      })
        .then(({ data }) => {
          this.model.config.columns = data.columns
          this.updateCols()
          this.loading = false
        })
        .catch(error => {
          this.loading = false
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
    },

    updateCols() {
      const { columns } = this.model.config

      columns.forEach(col => {
        const attribute = this.getMatchingAttribute(col.name)
        if (attribute && !this.isEdit) {
          this.$set(col, 'attribute', attribute)
        } else if (
          this.model.type === 'events' &&
          this.model.config.event === 'purchase'
        ) {
          this.$set(col, 'attribute', col.name)
        }
      })
    },

    getMatchingAttribute(columnName) {
      const attrs = this.steps[1].props.attributes
      const filterable = this.steps[1].props.filterableAttributes
      const grouped = this.steps[1].props.groupedFilterableAttributes
      const upperCaseName = columnName.toUpperCase()

      if (attrs.some(a => a.name.toUpperCase() === upperCaseName)) {
        return attrs.find(a => a.name.toUpperCase() === upperCaseName).name
      }

      const matchedFilterable = filterable.find(
        f => f.toUpperCase() === upperCaseName
      )
      if (matchedFilterable) {
        return matchedFilterable
      }

      for (const group of grouped) {
        const matchedGrouped = group.attributes.find(
          a => a.toUpperCase() === upperCaseName
        )
        if (matchedGrouped) {
          return matchedGrouped
        }
      }
    }
  }
}
</script>

<style scoped>
.custom-event {
  line-height: 3em;
}
</style>



// WEBPACK FOOTER //
// src/modules/imports/components/StepImportDataSource.vue